import { useState, useEffect, useMemo } from 'react'
import { useDebounce } from '@utils/useDebounce'
import { BuyerMemberFragment, useSearchBuyerMembersLazyQuery } from '@gql'

type UseBuyerMemberSearchResult = {
  searchQuery: string
  setSearchQuery: (query: string) => void
  buyerMembers: BuyerMemberFragment[]
  loading: boolean
  error?: Error
}

export const useBuyerMemberSearch = (
  debounceMs: number = 300
): UseBuyerMemberSearchResult => {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, debounceMs)

  const [searchBuyerMembers, { data, loading, error }] =
    useSearchBuyerMembersLazyQuery()

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 3) {
      const normalizedQuery = debouncedSearchQuery.trim().toLowerCase()
      const searchPattern = `%${normalizedQuery}%`

      searchBuyerMembers({
        variables: {
          displayNameSearch: searchPattern,
          emailSearch: searchPattern,
        },
      })
    } else {
      searchBuyerMembers({
        variables: {
          displayNameSearch: '',
          emailSearch: '',
        },
      })
    }
  }, [debouncedSearchQuery, searchBuyerMembers])

  const buyerMembers = useMemo(() => {
    const members = data?.buyer_member ?? []
    return members
  }, [data])

  return {
    searchQuery,
    setSearchQuery,
    buyerMembers,
    loading,
    error,
  }
}
